import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from 'src/context/AuthContext';
import { getStateName, getUserRole, isAdminUser } from 'src/utils/getInitials';
import { updateCompanyBranding } from 'src/redux/settingSlice';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';

const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const AccountProfile = ({ dialog = false, sx, ...props }) => {
  const { getUser } = useAuth();
  const user = getUser();
  const dispatch = useDispatch();
  const { appSettings } = useSelector((state) => state.settings);
  const canEditLogo = isAdminUser(getUser);
  const [uploading, setUploading] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handleLogoChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      dispatch(setErrorInfo({ errorMsg: 'Logo must be a PNG, JPEG, or WebP image.' }));
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      dispatch(setErrorInfo({ errorMsg: 'Logo must be 2 MB or smaller.' }));
      return;
    }

    setSelectedLogo(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearSelectedLogo = () => {
    setSelectedLogo(null);
    setPreviewUrl(null);
  };

  const handleSaveLogo = async () => {
    if (!selectedLogo) return;
    const formData = new FormData();
    formData.append('logo', selectedLogo);
    setUploading(true);
    try {
      await dispatch(updateCompanyBranding(formData)).unwrap();
      clearSelectedLogo();
      dispatch(setSuccessInfo({ successMsg: 'Company logo updated successfully.' }));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.logo?.[0] || error?.detail || 'Unable to update company logo.'
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    // Browser confirmation is intentional for this destructive action.
    // eslint-disable-next-line no-alert
    if (!appSettings?.company_logo_url || !window.confirm('Remove the company logo?')) return;
    const formData = new FormData();
    formData.append('remove_logo', 'true');
    setUploading(true);
    try {
      await dispatch(updateCompanyBranding(formData)).unwrap();
      dispatch(setSuccessInfo({ successMsg: 'Company logo removed successfully.' }));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.remove_logo?.[0] || error?.detail || 'Unable to remove company logo.'
      }));
    } finally {
      setUploading(false);
    }
  };

  const displayedLogo = previewUrl || appSettings?.company_logo_url || undefined;
  const profileName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
  const profileLocation = `${user?.city || ''}, ${getStateName(user?.state)}`.replace(/^, |, $/g, '');
  const avatarSize = dialog ? 176 : 100;

  return (
    <Card
      {...props}
      sx={{
        borderRadius: dialog ? 3 : undefined,
        bgcolor: dialog ? '#F3F7FF' : undefined,
        boxShadow: dialog ? 'none' : undefined,
        ...sx
      }}
    >
      <CardContent
        sx={{
          my: dialog ? 0 : 2,
          p: dialog ? { xs: 2.5, sm: 3 } : undefined,
          '&:last-child': dialog ? { pb: { xs: 2.5, sm: 3 } } : undefined
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={displayedLogo}
              alt={appSettings?.company_name || 'Company logo'}
              variant={dialog ? 'circular' : 'rounded'}
              sx={{
                height: avatarSize,
                width: avatarSize,
                p: dialog ? 1.5 : 1,
                bgcolor: 'background.paper',
                borderRadius: dialog ? '50%' : 2,
                overflow: 'hidden',
                '& .MuiAvatar-img': {
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: dialog ? '50%' : 1
                }
              }}
            >
              {appSettings?.company_name?.charAt(0)?.toUpperCase() || user?.first_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            {canEditLogo && (
              <IconButton
                component="label"
                aria-label="Update company logo"
                disabled={uploading}
                sx={{
                  position: 'absolute',
                  right: -6,
                  bottom: -6,
                  width: 34,
                  height: 34,
                  color: 'common.white',
                  bgcolor: 'primary.main',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {uploading ? <CircularProgress size={18} color="inherit" /> : <AddAPhotoIcon fontSize="small" />}
                <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} />
              </IconButton>
            )}
          </Box>
          {canEditLogo && !dialog && appSettings?.company_logo_url && !selectedLogo && (
            <Tooltip title="Remove company logo">
              <span>
                <IconButton
                  aria-label="Remove company logo"
                  color="error"
                  onClick={handleRemoveLogo}
                  disabled={uploading}
                  sx={{ mt: 1 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Typography
            color="textPrimary"
            gutterBottom
            variant={dialog ? 'h4' : 'h3'}
            sx={{ mt: dialog ? 2 : 1, fontWeight: 700, textAlign: 'center' }}
          >
            {profileName}
          </Typography>
          {dialog ? (
            <>
              <Chip
                label={getUserRole(user?.role)}
                size="small"
                sx={{
                  mt: 0.25,
                  color: 'primary.main',
                  bgcolor: '#E5EEFF',
                  fontWeight: 700,
                  borderRadius: 2
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.7,
                  mt: 1.5,
                  color: 'text.secondary'
                }}
              >
                <LocationOnOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">{profileLocation}</Typography>
              </Box>
              {canEditLogo && (
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                  startIcon={uploading ? <CircularProgress size={18} /> : <UploadFileIcon />}
                  disabled={uploading}
                  sx={{
                    mt: 3.5,
                    minHeight: 76,
                    justifyContent: 'flex-start',
                    px: 2,
                    textAlign: 'left',
                    color: 'text.primary',
                    borderColor: '#C7D5EC',
                    borderStyle: 'dashed',
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(255,255,255,0.85)'
                    }
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Change Profile Photo</Typography>
                    <Typography variant="caption" color="text.secondary">JPG, PNG, or WebP (Max 2MB)</Typography>
                  </Box>
                  <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} />
                </Button>
              )}
            </>
          ) : (
            <>
              <Typography color="textSecondary" variant="body1">
                {getUserRole(user?.role)}
              </Typography>
              <Typography color="textSecondary" variant="body1">
                {profileLocation}
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
      {!dialog && <Divider />}
      <Dialog open={Boolean(selectedLogo)} onClose={uploading ? undefined : clearSelectedLogo} maxWidth="xs" fullWidth>
        <DialogTitle>Save company logo?</DialogTitle>
        <DialogContent>
          <Box
            component="img"
            src={previewUrl || undefined}
            alt="Selected company logo preview"
            sx={{
              display: 'block',
              width: '100%',
              height: 220,
              objectFit: 'contain',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              bgcolor: '#FFFFFF'
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={clearSelectedLogo} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLogo} disabled={uploading}>
            {uploading ? <CircularProgress size={20} color="inherit" /> : 'Save photo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

AccountProfile.propTypes = {
  dialog: PropTypes.bool,
  sx: PropTypes.object
};

export default AccountProfile;
